// Vite bundles this worker file (and its @jitsi/rnnoise-wasm dependency) as an
// ES module chunk and returns the URL. ES format is required because rnnoise-sync.js
// uses import.meta.url at the top level (invalid in classic/iife scripts).
import NoiseSuppressorWorkletURL from './NoiseSuppressorWorklet?worker&url';

const PROCESSOR_NAME = 'NoiseSuppressorWorklet';

/**
 * Audio track effect that routes microphone audio through an AudioWorklet
 * running RNNoise ML-based noise suppression.
 *
 * Implements the startEffect / stopEffect interface expected by
 * JitsiLocalTrack.setEffect().
 *
 * IMPORTANT: call initialize() and await it before passing this to setEffect().
 * lib-jitsi-meet calls startEffect() synchronously — all async work (AudioContext
 * creation, worklet module loading) must be completed beforehand.
 */
export class NoiseSuppressionEffect {
  private _audioContext: AudioContext | null = null;
  private _sourceNode: MediaStreamAudioSourceNode | null = null;
  private _workletNode: AudioWorkletNode | null = null;
  private _destinationNode: MediaStreamAudioDestinationNode | null = null;

  /** Must be awaited before setEffect() is called. */
  async initialize(): Promise<void> {
    console.log(
      '[NoiseSuppression] Initializing — loading worklet from:',
      NoiseSuppressorWorkletURL
    );
    this._audioContext = new AudioContext({ sampleRate: 48000 });
    await this._audioContext.audioWorklet.addModule(NoiseSuppressorWorkletURL);
    console.log(
      '[NoiseSuppression] Worklet loaded OK. AudioContext state:',
      this._audioContext.state
    );
  }

  /** lib-jitsi-meet calls this before startEffect to decide whether to apply the effect. */
  isEnabled(jitsiLocalTrack: any): boolean {
    return jitsiLocalTrack.isAudioTrack();
  }

  /**
   * Synchronous — lib-jitsi-meet does not await this return value.
   * initialize() must have been called first so the AudioContext and worklet are ready.
   */
  startEffect(stream: MediaStream): MediaStream {
    if (!this._audioContext) {
      throw new Error('NoiseSuppressionEffect: initialize() was not called before startEffect()');
    }

    this._sourceNode = this._audioContext.createMediaStreamSource(stream);
    // RNNoise is a mono algorithm and the worklet only processes channel 0.
    // Force the node to a single channel: down-mix any stereo input (channelCount 1 +
    // explicit mode) and emit exactly one output channel (outputChannelCount [1]).
    // Without this, a stereo capture yields a 2-channel output whose right channel is
    // never written — remote listeners then hear audio on one side only.
    this._workletNode = new AudioWorkletNode(this._audioContext, PROCESSOR_NAME, {
      numberOfInputs: 1,
      numberOfOutputs: 1,
      channelCount: 1,
      channelCountMode: 'explicit',
      channelInterpretation: 'speakers',
      outputChannelCount: [1],
    });
    this._destinationNode = this._audioContext.createMediaStreamDestination();

    this._sourceNode.connect(this._workletNode);
    this._workletNode.connect(this._destinationNode);

    console.log(
      '[NoiseSuppression] Audio graph connected. Output stream tracks:',
      this._destinationNode.stream.getTracks().length
    );
    return this._destinationNode.stream;
  }

  stopEffect(): void {
    try {
      this._sourceNode?.disconnect();
      this._workletNode?.disconnect();
    } catch {
      // ignore disconnect errors on already-closed contexts
    }
    this._audioContext?.close().catch(() => {});
    this._audioContext = null;
    this._sourceNode = null;
    this._workletNode = null;
    this._destinationNode = null;
  }
}
